import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! Our advisor will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
        <div>
          <span className="section-label">Get in touch</span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mt-5">
            Book your <span className="gradient-text">free</span> consultation
          </h2>
          <p className="mt-5 text-slate-400 text-base sm:text-lg">
            A 30-minute discovery call with an AMFI-registered advisor — no commitment, no obligations.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { Icon: Phone, label: "Call us", value: "+91 78800 84126", href: "tel:+917880084126" },
              { Icon: Mail, label: "Email", value: "info@wealthoo.in", href: "mailto:info@wealthoo.in" },
              { Icon: MapPin, label: "Office", value: "514, Offizo, Magneto Mall, Telibandha, Raipur (C.G.) 492001", href: "#" },
            ].map((c, i) => (
              <a
                key={i}
                href={c.href}
                className="flex items-center gap-4 glass rounded-2xl p-5 hover:border-blue-500/40 transition-colors"
                data-testid={`contact-info-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                  <c.Icon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{c.label}</div>
                  <div className="font-bold mt-1">{c.value}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-6 rounded-2xl overflow-hidden border border-white/10 h-56">
            <iframe
              title="Wealthoo Office Location"
              src="https://maps.google.com/maps?q=Magneto%20Mall%20Telibandha%20Raipur&t=&z=14&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full"
              style={{ filter: "invert(0.9) hue-rotate(180deg)" }}
              data-testid="contact-map"
            />
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          onSubmit={submit}
          className="glass-strong rounded-3xl p-8 space-y-5 h-fit"
          data-testid="contact-form"
        >
          <h3 className="text-2xl font-bold">Tell us about your goals</h3>
          {[
            { name: "name", label: "Full Name", type: "text", placeholder: "Aarav Sharma" },
            { name: "email", label: "Email", type: "email", placeholder: "aarav@example.com" },
            { name: "phone", label: "Phone", type: "tel", placeholder: "+91 98765 43210" },
          ].map((f) => (
            <div key={f.name}>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{f.label}</label>
              <input
                type={f.type}
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                placeholder={f.placeholder}
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors"
                data-testid={`contact-${f.name}`}
              />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Message</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="I'm interested in starting a SIP for my child's education..."
              className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors resize-none"
              data-testid="contact-message"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="contact-submit"
          >
            {loading ? "Sending..." : (<>Send Message <Send className="w-4 h-4" /></>)}
          </button>
          <p className="text-xs text-slate-500 text-center">
            By submitting you agree to be contacted by Wealthoo. We never share your data.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
